import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  UsersThree,
  Briefcase,
  FilmStrip,
  StackSimple,
  HandWaving,
  BookOpen,
  Hand,
  Microphone,
  Lightning,  // (még használja a Mágnesposzt Varázsló kártya)
  CheckSquare,
  FileText,
  Question,
  CircleNotch,
} from "@phosphor-icons/react";
import AgentOpeningScreen from "../components/AgentOpeningScreen";
import MagnetPostFacebook from "./MagnetPostFacebook";
import MagnetPostLinkedIn from "./MagnetPostLinkedIn";
import MagnetPostReels from "./MagnetPostReels";
import MagnetPostCarousel from "./MagnetPostCarousel";
import EmailWelcomeSequence from "./EmailWelcomeSequence";
import EmailHandRaiser from "./EmailHandRaiser";
import EmailValueAdd from "./EmailValueAdd";
import EmailWebinar from "./EmailWebinar";
import LeadMagnetMicro from "./LeadMagnetMicro";
import { getActiveAlap } from "../lib/activeAlap";

// Vonzás layer accent — teal/cyan #75dff5. Used by all 3 Vonzás agents.
const TEAL_ACCENT = {
  eyebrow: "#75dff5",
  iconBg: "rgba(117, 223, 245, 0.15)",
  icon: "#75dff5",
  label: "#75dff5",
  selectedBg: "rgba(117, 223, 245, 0.10)",
  selectedBorder: "rgba(117, 223, 245, 0.50)",
  selectedIconBg: "rgba(117, 223, 245, 0.22)",
};

/* ─── 01 · Poszt Varázsló ─────────────────────────────────────── */

export function MagnetPostAgent() {
  const [selectedOpt, setSelectedOpt] = useState(null);
  // Wrapper display-mode mirror — a `MagnetPostFacebook` `onPhaseChange`
  // callback szinkronizálja ide az inline/full állapotát.
  //   "inline" → kártya-grid + alatta wrapper inline InputView (csak akkor,
  //              ha még nincs sem vázlat, sem kész poszt).
  //   "full"   → kártya-grid display:none, a wrapper teljes shell-t
  //              rendereli (PostHeader + Beállítás/Poszt tabok + tartalom)
  //              a sidebar mellett (NEM teljes viewport overlay).
  const [postDisplayMode, setPostDisplayMode] = useState("inline");

  // Spin-off átírási logika: amikor a felhasználó egy poszt-megjelenítőn
  // egy másik platform pillére kattint, ide érkezünk vissza a target
  // platform stringgel ("facebook" | "linkedin" | "instagram_reels" |
  // "carousel"). A sessionStorage handoff már be van állítva; itt csak
  // a selectedOpt-ot léptetjük, ami remountolja a célplatform wrapper
  // példányt — az pedig olvassa a handoffot és előtölti a setup-ot.
  const switchPlatform = (target) => {
    const TARGET_TO_OPT = {
      facebook: "facebook",
      linkedin: "linkedin",
      instagram_reels: "reels",
      carousel: "carousel",
    };
    const opt = TARGET_TO_OPT[target];
    if (!opt) return;
    // Force remount: először null-ra, majd a következő tickben a célra.
    // Ez biztosítja, hogy ha ugyanarra a wrapperre ugranánk (pl. valami
    // jövőbeni reaktiválás), a komponens biztosan remountol.
    setSelectedOpt(null);
    setPostDisplayMode("inline");
    setTimeout(() => setSelectedOpt(opt), 0);
  };

  const options = [
    { id: "facebook", title: "Facebook",  label: "Szöveges poszt",          icon: UsersThree },
    { id: "linkedin", title: "LinkedIn",  label: "Szöveges poszt",          icon: Briefcase },
    { id: "reels",    title: "IG Reels",  label: "Rövid videó ötlet és script", icon: FilmStrip },
    { id: "carousel", title: "Carousel",  label: "Képsorozat szövegekkel",  icon: StackSimple },
  ];

  // Mind a 4 platform → INLINE-HOST minta (Email Varázsló mintáját követve).
  //
  // A parent egy egyetlen `<div height:100vh flex flex-col>` shellt nyit a
  // `<main>` content-területén belül, így a bal oldali fő sidebar (Műhely /
  // Ügynökök / Identitás / Tudástár) végig látható — pontosan az
  // Email Agent vázlat- és eredmény-képernyőjének mintáját követve.
  //
  // A wrapper komponens (MagnetPostFacebook) EGYETLEN DOM-pozícióban él
  // (state-preservation), és önmaga dönti el a layoutját a saját phase-je
  // alapján:
  //   • phase=input → csak az inline InputView (max-w-820 mx-auto).
  //   • phase=output → PostHeader + Beállítás/Poszt tab + AnimatePresence,
  //                    kitöltve a parent flex-slotot (NEM `position:fixed`).
  //
  // A parent `postPhase`-re reagálva CSS-szel:
  //   • elrejti az AgentOpeningScreen-t output módban (display:none),
  //   • a wrapper-slotot input módban `flex:0 0 auto`-ra, output módban
  //     `flex:1`-re kapcsolja.
  return (
    <div
      className="flex flex-col"
      style={{ height: "100vh", background: "#0a0a12", overflow: "hidden" }}
      data-testid="magnet-post-agent-shell"
    >
      <div
        className="flex-1 min-h-0"
        style={{
          overflowY: postDisplayMode === "full" ? "hidden" : "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Opening grid + központi cím — full módban display:none */}
        <div style={{ display: postDisplayMode === "full" ? "none" : "block", flexShrink: 0 }}>
          <AgentOpeningScreen
            eyebrow="VONZÁS · 01 · POSZT"
            title="Poszt Varázsló"
            subtitle="Szia! Segítek olyan tartalmakat írni, amelyek megállítják a görgetést, nézőpontot váltanak, és elindítják a kommunikációt az ügyfeleiddel."
            options={options}
            columns={4}
            testidPrefix="magnet-post"
            accent={TEAL_ACCENT}
            controlledSelected={selectedOpt}
            onSelectOption={(opt) => {
              if (opt.id === "facebook") setSelectedOpt("facebook");
              if (opt.id === "linkedin") setSelectedOpt("linkedin");
              if (opt.id === "reels") setSelectedOpt("reels");
              if (opt.id === "carousel") setSelectedOpt("carousel");
            }}
            belowGrid={null}
          />
        </div>

        {/* Wrapper slot — egyetlen DOM-pozícióban, a platform-választás
            alapján a megfelelő wrapper komponens (Facebook / LinkedIn).
            A `key={selectedOpt}` garantálja a remount-ot platform-váltáskor
            (különben a state összemosódna eltérő platformok között). */}
        {(selectedOpt === "facebook" || selectedOpt === "linkedin" || selectedOpt === "reels" || selectedOpt === "carousel") && (
          <div
            style={{
              flex: postDisplayMode === "full" ? 1 : "0 0 auto",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
            data-testid={`magnet-post-${selectedOpt}-slot`}
          >
            {selectedOpt === "facebook" ? (
              <MagnetPostFacebook
                key="facebook"
                inlineHost
                onClose={() => { setSelectedOpt(null); setPostDisplayMode("inline"); }}
                onSwitchPlatform={switchPlatform}
                onPhaseChange={setPostDisplayMode}
                platform="facebook"
              />
            ) : selectedOpt === "linkedin" ? (
              <MagnetPostLinkedIn
                key="linkedin"
                inlineHost
                onClose={() => { setSelectedOpt(null); setPostDisplayMode("inline"); }}
                onSwitchPlatform={switchPlatform}
                onPhaseChange={setPostDisplayMode}
              />
            ) : selectedOpt === "reels" ? (
              <MagnetPostReels
                key="reels"
                inlineHost
                onClose={() => { setSelectedOpt(null); setPostDisplayMode("inline"); }}
                onSwitchPlatform={switchPlatform}
                onPhaseChange={setPostDisplayMode}
              />
            ) : (
              <MagnetPostCarousel
                key="carousel"
                inlineHost
                onClose={() => { setSelectedOpt(null); setPostDisplayMode("inline"); }}
                onSwitchPlatform={switchPlatform}
                onPhaseChange={setPostDisplayMode}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── 02 · Email Varázsló ───────────────────────────────────────────── */

// Az Email Varázsló — F3 mintát követi:
//   1) Kártya kattintás → inline kijelölés (lila glow + selected state),
//      a kártya-grid alatt megjelenik a connector + Generate gomb.
//   2) Generate kattintás → a gomb HELYBEN animálódik, a többi kártya
//      35%-ra halványul, a kiválasztott kártya kiemelve marad.
//   3) Generálás vége → átvált a 2-paneles dokumentum-megjelenítőre.
//
// Választás közbeni kártya-csere: az új kártya lesz kiválasztva (a regi
// inline panel a connector + Generate gombot az új típusra cseréli).

import { api as _api } from "../lib/api";
import MagicGenerateButton from "../components/MagicGenerateButton";
import { PostHeader } from "../pages/MagnetPostFacebook";

// Per-típus konfiguráció: connector mondat + generálás label + endpoint
// + a tab2 felirat (FB-mintán: "Üdvözlő szekvencia" stb., persistent).
// Új típus hozzáadása = új sor itt és a render komponens.
const EMAIL_TYPE_CONFIG = {
  welcome: {
    label: "Üdvözlő sorozat",
    connector: "Egy háromrészes üdvözlő e-mail sorozat készül az Avatarod, Ajánlatod és Márkahangod alapján.",
    generateLabel: "Üdvözlő sorozat generálása",
    generateEndpoint: "/attraction/email/welcome/generate",
    render: (props) => <EmailWelcomeSequence {...props} />,
  },
  value: {
    label: "Értékadó email",
    // ÉRTÉKADÓ pipeline = REUSE a poszt-facebook /topics + /sketches pipeline-t.
    // Inline setup (topic chips + saját téma + CTA) → /sketches → 3 vázlat
    // (krém outline page = EXISTING poszt outline) → /email/valueadd/full →
    // 1 értékadó e-mail Welcome-stílusú frame-ben.
    connector: "Téma + CTA + vázlat → egy magas minőségű értékadó email az aktív Foundation-edre.",
    generateLabel: "Email vázlatok generálása",
    // Ez NEM közvetlenül /full — a generate funkció /sketches-t hív és a
    // value típusnál a 3 vázlat lesz az első kimenet (EmailValueAdd kapja
    // initialSketches-ként). Az EmailValueAdd belül kezeli a sketch
    // pick → /email/valueadd/full lépést.
    generateEndpoint: "/attraction/post-facebook/sketches",
    render: (props) => <EmailValueAdd {...props} />,
  },
  callbook: {
    label: "Kézfelemelő csomag",
    connector: "Egy 4-emailes kézfelemelő csomag készül az Avatarod, Ajánlatod és Márkahangod alapján - egyetlen, közös válasz-kulcsszóval.",
    generateLabel: "Kézfelemelő csomag generálása",
    generateEndpoint: "/attraction/email/handraiser/generate",
    render: (props) => <EmailHandRaiser {...props} />,
    // A backend mező-neve "handraiser_content" — az alábbi key a wrapper
    // generálásban a megfelelő mezőhöz nyúl.
    contentKey: "handraiser_content",
  },
  webinar: {
    label: "Webinár promó",
    // WEBINÁR pipeline: phase-first input flow (mode picker) → dinamikus
    // inputok módonként → /attraction/email/webinar/generate → 5/6/5/16
    // email Welcome-stílusú sequence renderben. A handleGenerate
    // body-ja az AttractionAgents-ben épül fel (webinar-specifikus).
    connector: "Válassz egy fázist, add meg a webinár adatait, és kész a teljes szekvencia.",
    generateLabel: "Webinár szekvencia generálása",
    generateEndpoint: "/attraction/email/webinar/generate",
    render: (props) => <EmailWebinar {...props} />,
    contentKey: "webinar_content",
  },
};

// TONE-OPCIÓK az Üdvözlő sorozathoz (Q03 multi-select pills).
// Max 3 választható; 4. blokkolva (toast figyelmeztetéssel).
const WELCOME_TONE_OPTIONS = [
  "Baráti", "Szívélyes", "Játékos", "Humoros",
  "Profi",  "Közvetlen", "Inspiráló", "Higgadt",
];

// Layer-kék token a setup question style-okhoz — IDENTIKUS a poszt
// wizard "01 · MIRŐL SZÓLJON A POSZT?" eyebrow-jával.
const SETUP_TEAL = "#75dff5";
const SETUP_TEAL_BG = "rgba(117,223,245,0.06)";
const SETUP_TEAL_BG_ACTIVE = "rgba(117,223,245,0.16)";
const SETUP_TEAL_BORDER = "rgba(117,223,245,0.22)";
const SETUP_TEAL_BORDER_ACTIVE = "rgba(117,223,245,0.55)";

export function EmailAgent() {
  const [selectedOpt, setSelectedOpt] = useState(null);
  const [generating, setGenerating] = useState(false);
  // Élő gomb-felirat a job-driven generálás közben (Brief készítése… → E-mailek írása… 3/6).
  // Csak a webinar agent használja, a többi agentnél `null` marad és a default
  // „… készül…" feliratot adjuk vissza.
  const [generateLabel, setGenerateLabel] = useState(null);
  const [resultPayload, setResultPayload] = useState(null);
  const [activeTab, setActiveTab] = useState("tab1");

  // ── Welcome card pre-questions state (Case B) ──────────────────────
  // Mindhárom OPCIONÁLIS; üresen hagyva a writer a Foundation-re és
  // [helykitöltő]-re esik vissza.
  const [welcomeQ1, setWelcomeQ1] = useState("");
  const [welcomeQ2, setWelcomeQ2] = useState("");
  const [welcomeTones, setWelcomeTones] = useState([]);  // string[] — max 3

  // ── Értékadó (value) inline setup state ──────────────────────────────
  // REUSE post-facebook pipeline: topic chips + saját téma + CTA.
  const [valueTopics, setValueTopics] = useState([]);
  const [valueTopicObjects, setValueTopicObjects] = useState([]);
  const [valueTopicsLoading, setValueTopicsLoading] = useState(false);
  const [valueTopicsRegenerating, setValueTopicsRegenerating] = useState(false);
  const [valueSeenTopics, setValueSeenTopics] = useState([]);
  const [valueSelectedTopicChip, setValueSelectedTopicChip] = useState(null);
  const [valueSelectedTopicObject, setValueSelectedTopicObject] = useState(null);
  const [valueTema, setValueTema] = useState("");
  const [valueCtaId, setValueCtaId] = useState(null);

  // Topic chips betöltése a value kártya első kibontásakor.
  useEffect(() => {
    if (selectedOpt !== "value") return;
    if (valueTopics.length > 0 || valueTopicsLoading) return;
    let alive = true;
    const activeAlap = getActiveAlap() || {};
    (async () => {
      // setState calls are deferred into an async path so React's
      // hook-set-state-in-effect rule doesn't fire on the sync effect body.
      if (alive) setValueTopicsLoading(true);
      try {
        const res = await _api.post("/attraction/post-facebook/topics", {
          voice_id: activeAlap.voice || null,
          client_id: activeAlap.client || null,
          offer_id: activeAlap.offer || null,
        });
        const tt = Array.isArray(res.data?.topics) ? res.data.topics : [];
        const oo = Array.isArray(res.data?.topic_objects) ? res.data.topic_objects : [];
        if (alive) {
          setValueTopics(tt);
          setValueTopicObjects(oo);
          setValueSeenTopics(tt);
        }
      } catch {
        if (alive) { setValueTopics([]); setValueTopicObjects([]); }
      } finally {
        if (alive) setValueTopicsLoading(false);
      }
    })();
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOpt]);

  // ── Webinár (webinar) inline setup state ─────────────────────────────
  // Mode picker (promo / reminder / followup / full) + dinamikus inputok
  // mód szerint.
  const [webinarMode, setWebinarMode] = useState(null); // null | promo | reminder | followup | full
  const [webinarIdopont, setWebinarIdopont] = useState("");
  const [webinarCim, setWebinarCim] = useState("");
  const [webinarFoPontok, setWebinarFoPontok] = useState("");
  const [webinarFoFelismeresek, setWebinarFoFelismeresek] = useState("");
  const [webinarKovetkezoLepes, setWebinarKovetkezoLepes] = useState("");
  const [webinarDeadline, setWebinarDeadline] = useState("");
  const [webinarStilusPill, setWebinarStilusPill] = useState("");
  const [webinarStilusPillPromo, setWebinarStilusPillPromo] = useState("");
  const [webinarStilusPillFollowup, setWebinarStilusPillFollowup] = useState("");

  const regenerateValueTopics = async () => {
    if (valueTopicsRegenerating || valueTopicsLoading) return;
    setValueTopicsRegenerating(true);
    try {
      const activeAlap = getActiveAlap() || {};
      const res = await _api.post("/attraction/post-facebook/topics/regenerate", {
        voice_id: activeAlap.voice,
        client_id: activeAlap.client,
        offer_id: activeAlap.offer,
        previous_topics: valueSeenTopics,
      });
      const tt = Array.isArray(res.data?.topics) ? res.data.topics : [];
      const oo = Array.isArray(res.data?.topic_objects) ? res.data.topic_objects : [];
      if (tt.length) {
        setValueTopics(tt);
        setValueTopicObjects(oo);
        setValueSeenTopics((prev) => Array.from(new Set([...(prev || []), ...tt])));
      } else {
        const { toast } = await import("sonner");
        toast.error("Nem érkezett új téma. Próbáld újra.");
      }
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(e?.response?.data?.detail || "Új témák lekérése sikertelen.");
    } finally {
      setValueTopicsRegenerating(false);
    }
  };

  const options = [
    { id: "welcome",  title: "Üdvözlő sorozat",     label: "Építs bizalmat az új feliratkozóknál",                       icon: HandWaving },
    { id: "value",    title: "Értékadó email",      label: "Story-alapú levél, ami értéket ad és közelebb hoz",          icon: BookOpen },
    { id: "callbook", title: "Kézfelemelő email",   label: "Beszélgetésre hívó levél, ami választ és hívást generál",    icon: Hand },
    { id: "webinar",  title: "Webinár promó",       label: "Meghívó, emlékeztetők és utánkövetés a webinárodhoz",        icon: Microphone },
  ];

  const cfg = selectedOpt ? EMAIL_TYPE_CONFIG[selectedOpt] : null;
  const buttonState = generating ? "generating" : (selectedOpt && cfg ? "selected" : "idle");

  const handleGenerate = async () => {
    if (!cfg || generating) return;
    setGenerating(true);
    setGenerateLabel(null);
    try {
      // ÉRTÉKADÓ (value): /sketches → 3 vázlat, ezt adjuk az EmailValueAdd
      // initialSketches props-ban. NEM a kész e-mail, mert a user a
      // krém outline page-en választ vázlatot, és az EmailValueAdd hívja
      // a /email/valueadd/full-t a sketch click-jén.
      if (selectedOpt === "value") {
        const activeAlap = getActiveAlap() || {};
        const res = await _api.post(cfg.generateEndpoint, {
          voice_id: activeAlap.voice,
          client_id: activeAlap.client,
          offer_id: activeAlap.offer,
          tema: valueTema.trim(),
          cta_tipus: valueCtaId,
          aktiv_poszt: null,
          topic_object: valueSelectedTopicObject || null,
        });
        const vs = Array.isArray(res.data?.vazlatok) ? res.data.vazlatok : [];
        if (!vs.length) {
          const { toast } = await import("sonner");
          toast.error("Nem érkezett vázlat. Próbáld újra.");
          return;
        }
        setResultPayload({
          type: "value",
          initialSketches: vs,
          tema: valueTema.trim(),
          ctaTipus: valueCtaId,
          valasztottTopicObject: valueSelectedTopicObject,
        });
        setActiveTab("tab2");
        return;
      }

      // WEBINÁR (webinar): mode-specifikus inputokkal hív a
      // /attraction/email/webinar/generate-re. A response struktúrája:
      //   { teljes_szoveg, webinar_content: { emails: [...] }, metadata }
      // Az EmailWebinar komponens kap initialContent + initialRawText
      // + mode + inputs props-ot.
      //
      // FONTOS: ez egy 2-step pipeline (Brief Generator → Writer), két
      // SZEKVENCIÁLIS LLM hívással. Wall-clock 60-90s+ a Brief+Writer
      // futtatására. Ezért per-call timeout-ot 240s-re emeljük, hogy
      // a default 90s axios timeout ne szakítsa meg.
      if (selectedOpt === "webinar") {
        if (!webinarMode) {
          const { toast } = await import("sonner");
          toast.error("Válassz egy fázist először.");
          return;
        }
        const activeAlap = getActiveAlap() || {};
        const inputsObj = {
          webinar_idopont: webinarIdopont.trim(),
          webinar_cim: webinarCim.trim(),
          webinar_fo_pontok: webinarFoPontok.trim(),
          fo_felismeresek: webinarFoFelismeresek.trim(),
          kovetkezo_lepes: webinarKovetkezoLepes.trim(),
          deadline: webinarDeadline.trim(),
          stilus_pill: webinarStilusPill.trim(),
          stilus_pill_promo: webinarStilusPillPromo.trim(),
          stilus_pill_followup: webinarStilusPillFollowup.trim(),
        };
        console.info("[webinar] POST", cfg.generateEndpoint, { mode: webinarMode, inputs: inputsObj });
        // Új job-driven flow: az endpoint azonnal job_id-t ad vissza,
        // a generálás háttér-task-ban fut. A frontend a /generation/jobs/{id}
        // POLL-on figyeli a `progress` szöveget (a Generate gombhoz) és a
        // `partial.emails` listát (progresszív render).
        const startRes = await _api.post(cfg.generateEndpoint, {
          mode: webinarMode,
          voice_id: activeAlap.voice || null,
          client_id: activeAlap.client || null,
          offer_id: activeAlap.offer || null,
          ...inputsObj,
        });
        const jobId = startRes.data?.job_id;
        if (!jobId) {
          const { toast } = await import("sonner");
          toast.error("A generálás nem indult el (nincs job_id).");
          return;
        }
        // A felhasználó MARAD az input képernyőn, amíg az 5 (vagy N) e-mail
        // mind elkészül. A lila gombon élő státusz fut: előbb
        // „Brief készítése…", majd „E-mailek írása… 3/5". Csak a `done`
        // ágban váltunk át a result tab-ra, teljes contenttel - így soha
        // nem jelenik meg üres eredmény-képernyő.
        setGenerateLabel("Brief készítése…");

        const startedAt = Date.now();
        const TIMEOUT_MS = 6 * 60 * 1000; // 6 perc - a párhuzamos pipeline ennél bőven hamarabb végez
        while (Date.now() - startedAt < TIMEOUT_MS) {
          await new Promise((r) => setTimeout(r, 1200));
          let poll;
          try {
            poll = await _api.get(`/generation/jobs/${jobId}`, { timeout: 15000 });
          } catch (pe) {
            // egy-két átmeneti poll-hiba nem szakítja meg a flow-t
            continue;
          }
          const pdata = poll.data;
          if (pdata.status === "running") {
            const lbl = pdata.progress || "Generálás…";
            setGenerateLabel(lbl);
            continue;
          }
          if (pdata.status === "done") {
            const wc = pdata.result?.webinar_content || { emails: [] };
            const txt = pdata.result?.teljes_szoveg || "";
            if (!Array.isArray(wc.emails) || wc.emails.length === 0) {
              const { toast } = await import("sonner");
              toast.error("A backend nem adott vissza e-maileket. Próbáld újra.");
              return;
            }
            // Csak most ugrunk a result tab-ra, teljes contenttel.
            setResultPayload({
              type: "webinar",
              content: wc,
              rawText: txt,
              mode: webinarMode,
              inputs: inputsObj,
            });
            setActiveTab("tab2");
            return;
          }
          if (pdata.status === "error") {
            throw new Error(pdata.error || "Háttér-generálás sikertelen");
          }
        }
        throw new Error("Időtúllépés a webinár generáláson (>6 perc)");
      }

      // A pre-question válaszokat csak az üdvözlő sorozatnál küldjük.
      // Mind a 3 mező opcionális — üresen hagyva sem dob hibát a backend.
      const extras =
        selectedOpt === "welcome"
          ? {
              q1_contents: welcomeQ1.trim() || null,
              q2_channels: welcomeQ2.trim() || null,
              q3_tones: welcomeTones.length > 0 ? welcomeTones : null,
            }
          : {};
      const res = await _api.post(cfg.generateEndpoint, extras);
      // A backend mezőneve agent-függő: Welcome → welcome_content,
      // Kézfelemelő → handraiser_content. A cfg.contentKey override-olja.
      const contentField = cfg.contentKey || "welcome_content";
      const wc = res.data?.[contentField] || { emails: [] };
      const txt = res.data?.teljes_szoveg || "";
      setResultPayload({
        type: selectedOpt,
        content: Array.isArray(wc.emails) && wc.emails.length > 0 ? wc : null,
        rawText: txt,
      });
      setActiveTab("tab2");
    } catch (e) {
      // Klasszifikáljuk a hibát, hogy a user pontos visszajelzést kapjon.
      // Axios ECONNABORTED = timeout (a 90s vagy 240s limit lejárt). Ilyenkor
      // a backend még futhat, de a frontend már nem kapja meg a választ.
      // 502 = upstream LLM hiba (rate limit / budget / gateway). Egyéb 4xx/5xx
      // = backend-side validation/server error - a detail mezőből idézzük.
      let msg = "A generálás sikertelen volt.";
      if (e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "")) {
        msg = selectedOpt === "webinar"
          ? "Időtúllépés (a Brief + Writer együtt > 4 perc). Próbáld újra, vagy egyszerűsítsd a fő pontokat."
          : "Időtúllépés. Próbáld újra 1-2 perc múlva.";
      } else if (e?.response?.data?.detail) {
        msg = e.response.data.detail;
      } else if (e?.message) {
        msg = `Hiba: ${e.message}`;
      }
      console.error("[generate] failed:", e, "→ user msg:", msg);
      const { toast } = await import("sonner");
      toast.error(msg);
    } finally {
      setGenerating(false);
      setGenerateLabel(null);
    }
  };

  const toggleTone = (tone) => {
    setWelcomeTones((prev) => {
      if (prev.includes(tone)) return prev.filter((t) => t !== tone);
      if (prev.length >= 3) {
        // 4. választás blokkolva — diszkrét toast.
        import("sonner").then(({ toast }) => {
          toast.info("Maximum 3 hangvételt választhatsz.");
        });
        return prev;
      }
      return [...prev, tone];
    });
  };

  const navigate = useNavigate();
  // Vissza a fő agentek képernyőre — mindkét tabról ugyanaz.
  const onBackToOpening = () => navigate("/agents");

  // A header címe perzisztens: ha van kiválasztott típus, a típus neve
  // látszik (mint FB-n a "Facebook poszt"). Ha még nincs, "Email Varázsló".
  const headerTitle = cfg?.label || "Email Varázsló";
  // Tab2 felirat: a Poszt Varázsló "Poszt" mintáját követve az Email
  // tab is GENERIKUS — "Email" (NEM a típus neve), egységes minden
  // típushoz (Üdvözlő/Értékadó/Kézfelemelő/Webinar).
  const tab2Label = "Email";
  const tab2Disabled = !resultPayload;

  // A perzisztens chrome (PostHeader + tabok) CSAK akkor jelenik meg, ha
  // van eredmény, amire át lehet kapcsolni — pontosan úgy, mint a Poszt
  // Varázslóban. A landing screen (4 kártya) tiszta, mint a Mágnesposzt
  // Varázsló indító képernyő.
  const showChrome = !!resultPayload;

  // Card grid + inline expansion alatta (tab1 tartalma).
  // Az Üdvözlő sorozatnál a connector + Generate gomb FÖLÖTT 3 opcionális
  // pre-question accordion is megjelenik (Case B). A kérdések látszanak
  // generálás közben is — a felhasználó látja, miből indult; csak a
  // mezők disabled-eltek a generálás ideje alatt, hogy ne lehessen
  // futás közben módosítani őket.
  const showWelcomeQuestions = selectedOpt === "welcome" && cfg;

  const setupEyebrowStyle = {
    fontSize: 11, color: SETUP_TEAL, letterSpacing: "0.20em",
    textTransform: "uppercase", fontWeight: 600,
    display: "block", marginBottom: 18,
  };
  const setupInputStyle = {
    width: "100%", background: "rgba(255,255,255,0.025)",
    border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12,
    padding: "14px 16px", fontSize: 14, color: "#e8e8f0",
    fontFamily: "inherit", outline: "none",
    resize: "vertical", lineHeight: 1.55,
  };

  const inlineBelow = selectedOpt && cfg ? (
    <div className="mx-auto" style={{ width: "100%", maxWidth: 720, padding: "0 8px", position: "relative" }} data-testid="email-inline-expand">
      {showWelcomeQuestions && (
        <div
          style={{
            width: "100%", textAlign: "left",
            marginTop: 48, marginBottom: 36,
            opacity: generating ? 0.55 : 1,
            pointerEvents: generating ? "none" : "auto",
            transition: "opacity 0.22s ease",
          }}
          data-testid="email-welcome-questions"
        >
          {/* Q1 — szabad szöveg */}
          <div style={{ marginTop: 8, marginBottom: 36 }}>
            <label style={setupEyebrowStyle}>
              01 · MILYEN TARTALMAT AJÁNLANÁL EGY ÚJ FELIRATKOZÓNAK?
            </label>
            <textarea
              value={welcomeQ1}
              onChange={(e) => setWelcomeQ1(e.target.value)}
              data-testid="email-welcome-q1"
              placeholder="Nyugodtan ossz meg 2-3 tartalmat, akár linkkel..."
              rows={3}
              style={setupInputStyle}
            />
          </div>

          {/* Q2 — szabad szöveg */}
          <div style={{ marginBottom: 36 }}>
            <label style={setupEyebrowStyle}>
              02 · MELY CSATORNÁKON VAGY MÉG AKTÍV?
            </label>
            <textarea
              value={welcomeQ2}
              onChange={(e) => setWelcomeQ2(e.target.value)}
              data-testid="email-welcome-q2"
              placeholder="Pl. Instagram, YouTube, podcast, blog..."
              rows={2}
              style={setupInputStyle}
            />
          </div>

          {/* Q3 — multi-select pillek (max 3) */}
          <div style={{ marginBottom: 8 }}>
            <label style={setupEyebrowStyle}>
              03 · MILYEN HANGVÉTELŰ LEGYEN A SOROZAT?
            </label>
            <div className="flex flex-wrap gap-2.5" data-testid="email-welcome-q3-pills">
              {WELCOME_TONE_OPTIONS.map((tone, idx) => {
                const active = welcomeTones.includes(tone);
                const tid = `email-welcome-tone-${idx}`;
                return (
                  <button
                    key={tone}
                    type="button"
                    onClick={() => toggleTone(tone)}
                    data-testid={tid}
                    data-tone={tone}
                    data-active={active ? "true" : "false"}
                    style={{
                      fontSize: 12.5, padding: "9px 15px", borderRadius: 999,
                      background: active ? SETUP_TEAL_BG_ACTIVE : SETUP_TEAL_BG,
                      border: `1px solid ${active ? SETUP_TEAL_BORDER_ACTIVE : SETUP_TEAL_BORDER}`,
                      color: active ? "#cef1fb" : SETUP_TEAL,
                      cursor: "pointer", fontFamily: "inherit", fontWeight: 500,
                      transition: "all 0.16s",
                    }}
                  >
                    {tone}
                  </button>
                );
              })}
            </div>
            <div style={{ marginTop: 10, fontSize: 11, color: "#6b6b80", letterSpacing: "0.01em" }}>
              {welcomeTones.length}/3 választva — mind opcionális.
            </div>
          </div>
        </div>
      )}

      {/* ─── ÉRTÉKADÓ (value) inline setup ─────────────────────────── */}
      {selectedOpt === "value" && cfg && (
        <ValueInlineSetup
          tema={valueTema} setTema={setValueTema}
          topics={valueTopics} topicObjects={valueTopicObjects}
          topicsLoading={valueTopicsLoading}
          topicsRegenerating={valueTopicsRegenerating}
          selectedTopicChip={valueSelectedTopicChip}
          setSelectedTopicChip={setValueSelectedTopicChip}
          setSelectedTopicObject={setValueSelectedTopicObject}
          onRegenerateTopics={regenerateValueTopics}
          ctaId={valueCtaId} setCtaId={setValueCtaId}
          generating={generating}
        />
      )}

      {/* ─── WEBINÁR (webinar) inline setup ────────────────────────── */}
      {selectedOpt === "webinar" && cfg && (
        <WebinarInlineSetup
          mode={webinarMode} setMode={setWebinarMode}
          webinarIdopont={webinarIdopont} setWebinarIdopont={setWebinarIdopont}
          webinarCim={webinarCim} setWebinarCim={setWebinarCim}
          webinarFoPontok={webinarFoPontok} setWebinarFoPontok={setWebinarFoPontok}
          foFelismeresek={webinarFoFelismeresek} setFoFelismeresek={setWebinarFoFelismeresek}
          kovetkezoLepes={webinarKovetkezoLepes} setKovetkezoLepes={setWebinarKovetkezoLepes}
          deadline={webinarDeadline} setDeadline={setWebinarDeadline}
          stilusPill={webinarStilusPill} setStilusPill={setWebinarStilusPill}
          stilusPillPromo={webinarStilusPillPromo} setStilusPillPromo={setWebinarStilusPillPromo}
          stilusPillFollowup={webinarStilusPillFollowup} setStilusPillFollowup={setWebinarStilusPillFollowup}
          generating={generating}
        />
      )}

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 22, textAlign: "center" }}>
        {!showWelcomeQuestions && selectedOpt !== "value" && selectedOpt !== "webinar" && (
          <p style={{ fontSize: 14, color: "#9898ad", lineHeight: 1.65, maxWidth: 540, margin: 0 }}
             data-testid="email-inline-connector">
            {cfg.connector}
          </p>
        )}
        <MagicGenerateButton
          state={
            generating
              ? buttonState
              : selectedOpt === "value" && (!valueTema.trim() || !valueCtaId)
              ? "idle"
              : selectedOpt === "webinar" && (
                  !webinarMode ||
                  (webinarMode === "promo" && !webinarCim.trim()) ||
                  (webinarMode === "reminder" && !webinarCim.trim())
                )
              ? "idle"
              : buttonState
          }
          label={generating ? (generateLabel || `${cfg.label} készül…`) : cfg.generateLabel}
          onClick={handleGenerate}
          testid="email-generate-button"
        />
      </div>
    </div>
  ) : null;

  return (
    <div className="flex flex-col" style={{ height: "100vh", background: "#0a0a12", overflow: "hidden" }}
         data-testid="email-agent-shell">
      <>
        {showChrome && (
            <PostHeader
              onBackToOpening={onBackToOpening}
              activeTab={activeTab}
              onTabChange={(t) => {
                if (t === "tab2" && tab2Disabled) return;
                setActiveTab(t);
              }}
              tab2Disabled={tab2Disabled}
              headerTitle={headerTitle}
              tab2Label={tab2Label}
            />
          )}

          <div className="flex-1 min-h-0" style={{ overflow: "hidden" }}>
            {activeTab === "tab1" ? (
              <div className="h-full overflow-y-auto">
                <AgentOpeningScreen
                  eyebrow="VONZÁS · 02 · EMAIL"
                  title="Email Varázsló"
                  subtitle="Szia! Írjunk olyan leveleket, amelyek nem vesznek el a promóciók között, hanem valódi figyelmet, bizalmat és válaszokat generálnak."
                  options={options}
                  columns={4}
                  testidPrefix="email"
                  accent={TEAL_ACCENT}
                  controlledSelected={selectedOpt}
                  onSelectOption={(opt) => {
                    if (!opt || opt.locked || generating) return;
                    if (!EMAIL_TYPE_CONFIG[opt.id]) {
                      import("sonner").then(({ toast }) => {
                        toast.info(`${opt.title} hamarosan elérhető.`);
                      });
                    }
                    setSelectedOpt(opt.id);
                  }}
                  belowGrid={inlineBelow}
                />
              </div>
            ) : (
              // Tab2: result document. A render konfiguráció típus-függő:
              //  • welcome / callbook → initialContent + initialRawText
              //  • value              → initialSketches + tema + ctaTipus
              //                         + valasztottTopicObject
              resultPayload && cfg && (
                resultPayload.type === "value"
                  ? cfg.render({
                      initialSketches: resultPayload.initialSketches || [],
                      tema: resultPayload.tema || "",
                      ctaTipus: resultPayload.ctaTipus || "",
                      valasztottTopicObject: resultPayload.valasztottTopicObject || null,
                    })
                  : resultPayload.type === "webinar"
                  ? cfg.render({
                      initialContent: resultPayload.content,
                      initialRawText: resultPayload.rawText,
                      mode: resultPayload.mode,
                      inputs: resultPayload.inputs || {},
                    })
                  : cfg.render({
                      initialContent: resultPayload.content,
                      initialRawText: resultPayload.rawText,
                    })
              )
            )}
          </div>
      </>
    </div>
  );
}

/* ─── 03 · Ügyfélmágnes Varázsló ────────────────────────────────────── */

// Az Ügyfélmágnes Varázsló az Email Varázsló inline-expand mintáját követi:
// EGYETLEN kártya (Mikromágnes) — kattintásra alatta inline kibomlik a
// téma-generátor (01) + formátum-pillek (02) + Generate gomb.
// Generate → 3 vázlat (3 szög: Gyors Gyakorlat / Mentális Tehermentesítő
// / Sürgősségi Elsősegély) → felhasználó választ → kész MIKROMÁGNES +
// külön kódblokk-szerű KÉZFELTARTÓS POSZT, lila refine panellel.

// A formátum-pillék kivéve a UX-ből — a Vázlat-Generátor választ vázlatonként
// a 10 megengedett formátum-címkéből (backend `_LEAD_MAGNET_FORMAT_LABELS`).
// A sketch.format mező hordozza a választott formátum nevét a kártyához.

export function LeadMagnetAgent() {
  const [selectedOpt, setSelectedOpt] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [resultPayload, setResultPayload] = useState(null);
  const [activeTab, setActiveTab] = useState("tab1");

  // ── Mikromágnes setup state ──────────────────────────────────────────
  const [lmTopics, setLmTopics] = useState([]);
  const [lmTopicObjects, setLmTopicObjects] = useState([]);
  const [lmTopicsLoading, setLmTopicsLoading] = useState(false);
  const [lmTopicsRegenerating, setLmTopicsRegenerating] = useState(false);
  const [lmSeenTopics, setLmSeenTopics] = useState([]);
  const [lmSelectedTopicChip, setLmSelectedTopicChip] = useState(null);
  const [lmSelectedTopicObject, setLmSelectedTopicObject] = useState(null);
  // Riportzárás módja: a felhasználó az input képernyőn választja ki.
  const [lmReportClosingMode, setLmReportClosingMode] = useState("clean_close");
  const [lmTema, setLmTema] = useState("");
  // A `02 · MILYEN FORMÁTUMBAN?` kérdés kivéve — a formátumot a Vázlat-
  // Generátor választja vázlatonként és visszaadja a sketch.format mezőben.

  // Topic chips betöltése a Mikromágnes kártya első kibontásakor.
  // SAJÁT, mikromágnes-specifikus generátor (`/attraction/lead-magnet/topics`)
  // — NEM a poszt-/email téma-generátor. A pille a `title`-t mutatja; a
  // `trigger_scene` és `instant_relief` mezőket továbbvisszük a vázlat-
  // generálás hívásába (`topic_object`).
  useEffect(() => {
    if (selectedOpt !== "mikromagnet") return;
    if (lmTopics.length > 0 || lmTopicsLoading) return;
    let alive = true;
    const activeAlap = getActiveAlap() || {};
    (async () => {
      if (alive) setLmTopicsLoading(true);
      try {
        const res = await _api.post("/attraction/lead-magnet/topics", {
          voice_id: activeAlap.voice || null,
          client_id: activeAlap.client || null,
          offer_id: activeAlap.offer || null,
        });
        const tt = Array.isArray(res.data?.topics) ? res.data.topics : [];
        const oo = Array.isArray(res.data?.topic_objects) ? res.data.topic_objects : [];
        if (alive) {
          setLmTopics(tt); setLmTopicObjects(oo); setLmSeenTopics(tt);
        }
      } catch {
        if (alive) { setLmTopics([]); setLmTopicObjects([]); }
      } finally {
        if (alive) setLmTopicsLoading(false);
      }
    })();
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOpt]);

  const regenerateLmTopics = async () => {
    if (lmTopicsRegenerating || lmTopicsLoading) return;
    setLmTopicsRegenerating(true);
    try {
      const activeAlap = getActiveAlap() || {};
      const res = await _api.post("/attraction/lead-magnet/topics/regenerate", {
        voice_id: activeAlap.voice,
        client_id: activeAlap.client,
        offer_id: activeAlap.offer,
        previous_topics: lmSeenTopics,
      });
      const tt = Array.isArray(res.data?.topics) ? res.data.topics : [];
      const oo = Array.isArray(res.data?.topic_objects) ? res.data.topic_objects : [];
      if (tt.length) {
        setLmTopics(tt); setLmTopicObjects(oo);
        setLmSeenTopics((prev) => Array.from(new Set([...(prev || []), ...tt])));
      } else {
        const { toast } = await import("sonner");
        toast.error("Nem érkezett új téma. Próbáld újra.");
      }
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(e?.response?.data?.detail || "Új témák lekérése sikertelen.");
    } finally {
      setLmTopicsRegenerating(false);
    }
  };

  // EGYETLEN kártya — a többi formátumot (mini-guide / kvíz / handraiser)
  // a felhasználó kérésére kivettük, később hozzuk vissza.
  const options = [
    {
      id: "mikromagnet",
      title: "Mikromágnes™",
      label: "Juttasd a közönséged gyors győzelemhez 10 perc alatt",
      icon: Lightning,
    },
  ];

  const handleGenerate = async () => {
    if (selectedOpt !== "mikromagnet" || generating) return;
    if (!lmTema.trim()) return;
    setGenerating(true);
    try {
      const activeAlap = getActiveAlap() || {};
      const res = await _api.post("/attraction/lead-magnet/sketches", {
        voice_id: activeAlap.voice,
        client_id: activeAlap.client,
        offer_id: activeAlap.offer,
        tema: lmTema.trim(),
        topic_object: lmSelectedTopicObject || null,
        // user_focus akkor van, ha a felhasználó saját szöveget írt
        // (NEM kattintott pille-re). A backend vagy a topic_object-et
        // használja, vagy a user_focus-t (ha topic_object null).
        user_focus: lmSelectedTopicObject ? "" : lmTema.trim(),
      });
      const vs = Array.isArray(res.data?.vazlatok) ? res.data.vazlatok : [];
      if (!vs.length) {
        const { toast } = await import("sonner");
        toast.error("Nem érkezett vázlat. Próbáld újra.");
        return;
      }
      setResultPayload({
        type: "mikromagnet",
        initialSketches: vs,
        tema: lmTema.trim(),
        valasztottTopicObject: lmSelectedTopicObject,
        reportClosingMode: lmReportClosingMode,
      });
      setActiveTab("tab2");
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(e?.response?.data?.detail || "A generálás sikertelen.");
    } finally {
      setGenerating(false);
    }
  };

  const navigate = useNavigate();
  const onBackToOpening = () => navigate("/agents");

  const headerTitle = "Ügyfélmágnes Varázsló";
  const tab2Label = "Mikromágnes™";
  const tab2Disabled = !resultPayload;
  const showChrome = !!resultPayload;

  const eyebrowStyle = {
    fontSize: 11, color: SETUP_TEAL, letterSpacing: "0.20em",
    textTransform: "uppercase", fontWeight: 600,
    display: "block", marginBottom: 18,
  };
  const inputStyle = {
    width: "100%", background: "rgba(255,255,255,0.025)",
    border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12,
    padding: "14px 16px", fontSize: 14, color: "#e8e8f0",
    fontFamily: "inherit", outline: "none",
    resize: "vertical", lineHeight: 1.55,
  };

  const inlineBelow = selectedOpt === "mikromagnet" ? (
    <div className="mx-auto" style={{ width: "100%", maxWidth: 720, padding: "0 8px" }} data-testid="lead-magnet-inline-expand">
      <div
        style={{
          width: "100%", textAlign: "left",
          marginTop: 48, marginBottom: 36,
          opacity: generating ? 0.55 : 1,
          pointerEvents: generating ? "none" : "auto",
          transition: "opacity 0.22s ease",
        }}
        data-testid="lead-magnet-questions"
      >
        {/* 01 · MILYEN GYORS GYŐZELMET ADJUNK? — pontosan az Értékadó téma-pille
            mintája. A „02 · MILYEN FORMÁTUMBAN?" kérdés kivéve: a formátumot
            mostantól a Vázlat-Generátor választja vázlatonként. */}
        <div style={{ marginTop: 8, marginBottom: 8 }}>
          <label style={eyebrowStyle}>01 · MILYEN GYORS GYŐZELMET ADJUNK?</label>
          {lmTopicsLoading ? (
            <div className="flex items-center gap-2 text-[12.5px] text-[#9898ad] mb-4">
              <CircleNotch size={13} className="animate-spin" /> Téma-javaslatok generálása…
            </div>
          ) : lmTopics.length > 0 ? (
            <>
              <div className="flex flex-wrap gap-2.5 mb-3" data-testid="lead-magnet-topic-chips">
                {lmTopics.map((raw) => {
                  const t = String(raw || "").replace(/\s*\[.+?\]\s*$/g, "").trim();
                  const active = lmSelectedTopicChip === t;
                  const obj = (lmTopicObjects || []).find((o) => (o?.title || "").trim() === t.trim()) || null;
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => {
                        setLmSelectedTopicChip(t);
                        setLmTema(t);
                        setLmSelectedTopicObject(obj || null);
                      }}
                      data-testid={`lead-magnet-topic-chip-${t.toLowerCase().replace(/\s+/g, "-").slice(0, 30)}`}
                      style={{
                        fontSize: 12.5, padding: "9px 15px", borderRadius: 999,
                        background: active ? SETUP_TEAL_BG_ACTIVE : SETUP_TEAL_BG,
                        border: `1px solid ${active ? SETUP_TEAL_BORDER_ACTIVE : SETUP_TEAL_BORDER}`,
                        color: active ? "#cef1fb" : SETUP_TEAL,
                        cursor: "pointer", fontFamily: "inherit", fontWeight: 500,
                        transition: "all 0.16s",
                        textAlign: "left",
                      }}
                    >{t}</button>
                  );
                })}
              </div>
              <div className="flex justify-start mb-7" style={{ marginTop: 4 }}>
                <button
                  type="button"
                  onClick={regenerateLmTopics}
                  disabled={lmTopicsRegenerating}
                  data-testid="lead-magnet-topics-regenerate"
                  style={{
                    fontSize: 12, padding: "8px 16px", borderRadius: 999,
                    background: "transparent",
                    border: "1px dashed rgba(117,223,245,0.35)",
                    color: lmTopicsRegenerating ? "#5a5a6a" : SETUP_TEAL,
                    cursor: lmTopicsRegenerating ? "wait" : "pointer",
                    fontFamily: "inherit", fontWeight: 500, letterSpacing: "0.04em",
                    display: "inline-flex", alignItems: "center", gap: 6,
                    opacity: lmTopicsRegenerating ? 0.7 : 1,
                  }}
                >
                  {lmTopicsRegenerating ? (
                    <><CircleNotch size={12} className="animate-spin" /> Új témák…</>
                  ) : (
                    <>↻ Még témákat</>
                  )}
                </button>
              </div>
            </>
          ) : (
            <div className="text-[12.5px] text-[#9898ad] mb-4">
              Nem érkezett téma-javaslat. Írj saját témát alább.
            </div>
          )}
          <textarea
            value={lmTema}
            onChange={(e) => { setLmTema(e.target.value); setLmSelectedTopicChip(null); setLmSelectedTopicObject(null); }}
            placeholder={`Saját gyors győzelem… pl. „Délutáni mélypontból visszatalálni"`}
            rows={3}
            data-testid="lead-magnet-tema-input"
            style={inputStyle}
          />
        </div>
      </div>

      {/* 02 · Hogyan zárjon a riport? - 2 kártya zárás-mód választó */}
      <div data-testid="lead-magnet-closing-mode-section" style={{ marginTop: 28 }}>
        <div style={{
          fontSize: 11, color: "#7a99a3", letterSpacing: "0.20em",
          fontWeight: 600, textTransform: "uppercase", marginBottom: 14,
        }}>
          02 · Hogyan zárjon a riport?
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {[
            { id: "clean_close", title: "Szép lezárás", desc: "Elegánsan lezár, további lépés nélkül. Az olvasónak megmarad a mini győzelem érzése." },
            { id: "keyword_bridge", title: "Kulcsszavas továbbvezetés", desc: "A végén egy privát üzenetes kulcsszóval terel. A kulcsszót a rendszer választja." },
          ].map((opt) => {
            const active = lmReportClosingMode === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setLmReportClosingMode(opt.id)}
                data-testid={`lead-magnet-closing-mode-${opt.id}`}
                style={{
                  textAlign: "left",
                  padding: "16px 16px 14px",
                  borderRadius: 12,
                  background: active ? SETUP_TEAL_BG_ACTIVE : SETUP_TEAL_BG,
                  border: `1px solid ${active ? SETUP_TEAL_BORDER_ACTIVE : SETUP_TEAL_BORDER}`,
                  color: active ? "#cef1fb" : "#a5c4cc",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  transition: "all 0.16s",
                }}
              >
                <div style={{
                  fontSize: 13, fontWeight: 600, marginBottom: 4,
                  color: active ? "#cef1fb" : SETUP_TEAL,
                }}>
                  {opt.title}
                </div>
                <div style={{
                  fontSize: 11.5, lineHeight: 1.45,
                  color: active ? "#9fd4e0" : "#7a99a3",
                }}>
                  {opt.desc}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 22, textAlign: "center", marginTop: 28 }}>
        <MagicGenerateButton
          state={
            generating
              ? "generating"
              : !lmTema.trim()
              ? "idle"
              : "selected"
          }
          label={generating ? "Mikromágnes készül…" : "Mikromágnes generálása"}
          onClick={handleGenerate}
          testid="lead-magnet-generate-button"
        />
      </div>
    </div>
  ) : null;

  return (
    <div className="flex flex-col" style={{ height: "100vh", background: "#0a0a12", overflow: "hidden" }}
         data-testid="lead-magnet-agent-shell">
      {showChrome && (
        <PostHeader
          onBackToOpening={onBackToOpening}
          activeTab={activeTab}
          onTabChange={(t) => {
            if (t === "tab2" && tab2Disabled) return;
            setActiveTab(t);
          }}
          tab2Disabled={tab2Disabled}
          headerTitle={headerTitle}
          tab2Label={tab2Label}
        />
      )}

      <div className="flex-1 min-h-0" style={{ overflow: "hidden" }}>
        {activeTab === "tab1" ? (
          <div className="h-full overflow-y-auto">
            <AgentOpeningScreen
              eyebrow="VONZÁS · 03 · MÁGNES"
              title="Ügyfélmágnes Varázsló"
              subtitle="Szia! Készítsünk egy értékes mini anyagot, amit az érdeklődőd örömmel elkér, és amiből azonnal megérzi, milyen nagyszerű lenne veled dolgozni."
              options={options}
              columns={4}
              centerOptions
              testidPrefix="lead-magnet"
              accent={TEAL_ACCENT}
              controlledSelected={selectedOpt}
              onSelectOption={(opt) => {
                if (!opt || opt.locked || generating) return;
                setSelectedOpt(opt.id);
              }}
              belowGrid={inlineBelow}
            />
          </div>
        ) : (
          resultPayload && (
            <LeadMagnetMicro
              initialSketches={resultPayload.initialSketches || []}
              tema={resultPayload.tema || ""}
              valasztottTopicObject={resultPayload.valasztottTopicObject || null}
            />
          )
        )}
      </div>
    </div>
  );
}

/* ─── ValueInlineSetup — topic chips + saját téma + CTA selector ──────
   Az Email Varázsló landing page-én jelenik meg INLINE a `value` kártya
   alatt — a Welcome 3 kérdés inline-reveal mintáját követi.
   A felhasználó kiválaszt egy témát (chip vagy saját), egy CTA-t, és a
   Generate gomb a /sketches endpoint-ot hívja. */

const VALUE_CTA_OPTIONS = [
  { id: "beszelgetes", title: "Beszélgetésindító", desc: "Kérdéssel zár — válaszra / privát üzenetre hív." },
  { id: "ingyenes",    title: "Ingyenes tartalom", desc: "Ingyenes tartalomra vagy első lépésre invitál." },
  { id: "jelentkezes", title: "Jelentkezés ajánlatra", desc: "Meghívás a konkrét ajánlatodra." },
];

function cleanValueTopic(s) {
  return String(s || "").replace(/\s*\[.+?\]\s*$/g, "").trim();
}

function ValueInlineSetup({
  tema, setTema,
  topics, topicObjects, topicsLoading, topicsRegenerating,
  selectedTopicChip, setSelectedTopicChip, setSelectedTopicObject,
  onRegenerateTopics,
  ctaId, setCtaId,
  generating,
}) {
  const eyebrowStyle = {
    fontSize: 11, color: SETUP_TEAL, letterSpacing: "0.20em",
    textTransform: "uppercase", fontWeight: 600,
    display: "block", marginBottom: 18,
  };
  const inputStyle = {
    width: "100%", background: "rgba(255,255,255,0.025)",
    border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12,
    padding: "14px 16px", fontSize: 14, color: "#e8e8f0",
    fontFamily: "inherit", outline: "none",
    resize: "vertical", lineHeight: 1.55,
  };
  return (
    <div
      style={{
        width: "100%", textAlign: "left",
        marginTop: 48, marginBottom: 36,
        opacity: generating ? 0.55 : 1,
        pointerEvents: generating ? "none" : "auto",
        transition: "opacity 0.22s ease",
      }}
      data-testid="email-value-questions"
    >
      {/* Q1 — TÉMA */}
      <div style={{ marginTop: 8, marginBottom: 36 }}>
        <label style={eyebrowStyle}>
          01 · MIRŐL SZÓLJON AZ E-MAIL?
        </label>
        {topicsLoading ? (
          <div className="flex items-center gap-2 text-[12.5px] text-[#9898ad] mb-4">
            <CircleNotch size={13} className="animate-spin" /> Téma-javaslatok generálása…
          </div>
        ) : topics.length > 0 ? (
          <>
            <div className="flex flex-wrap gap-2.5 mb-3" data-testid="email-value-topic-chips">
              {topics.map((raw) => {
                const t = cleanValueTopic(raw);
                const active = selectedTopicChip === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => {
                      setSelectedTopicChip(t);
                      setTema(t);
                      const obj = (topicObjects || []).find((o) => (o?.title || "").trim() === t.trim());
                      setSelectedTopicObject(obj || null);
                    }}
                    data-testid={`email-value-chip-${t.toLowerCase().replace(/\s+/g, "-").slice(0, 30)}`}
                    style={{
                      fontSize: 12.5, padding: "9px 15px", borderRadius: 999,
                      background: active ? SETUP_TEAL_BG_ACTIVE : SETUP_TEAL_BG,
                      border: `1px solid ${active ? SETUP_TEAL_BORDER_ACTIVE : SETUP_TEAL_BORDER}`,
                      color: active ? "#cef1fb" : SETUP_TEAL,
                      cursor: "pointer", fontFamily: "inherit", fontWeight: 500,
                      transition: "all 0.16s",
                    }}
                  >{t}</button>
                );
              })}
            </div>
            <div className="flex justify-start mb-7" style={{ marginTop: 4 }}>
              <button
                type="button"
                onClick={onRegenerateTopics}
                disabled={topicsRegenerating}
                data-testid="email-value-topics-regenerate"
                style={{
                  fontSize: 12, padding: "8px 16px", borderRadius: 999,
                  background: "transparent",
                  border: "1px dashed rgba(117,223,245,0.35)",
                  color: topicsRegenerating ? "#5a5a6a" : SETUP_TEAL,
                  cursor: topicsRegenerating ? "wait" : "pointer",
                  fontFamily: "inherit", fontWeight: 500, letterSpacing: "0.04em",
                  display: "inline-flex", alignItems: "center", gap: 6,
                  opacity: topicsRegenerating ? 0.7 : 1,
                }}
              >
                {topicsRegenerating ? (
                  <><CircleNotch size={12} className="animate-spin" /> Új témák…</>
                ) : (
                  <>↻ Még témákat</>
                )}
              </button>
            </div>
          </>
        ) : (
          <div className="text-[12.5px] text-[#9898ad] mb-4">
            Nem érkezett téma-javaslat. Írj saját témát alább.
          </div>
        )}
        <textarea
          value={tema}
          onChange={(e) => { setTema(e.target.value); setSelectedTopicChip(null); setSelectedTopicObject(null); }}
          placeholder={`Vagy írj saját témát: pl. „Az értékes tartalom csapdája"`}
          rows={3}
          data-testid="email-value-tema-input"
          style={inputStyle}
        />
      </div>

      {/* Q2 — CTA */}
      <div style={{ marginBottom: 8 }}>
        <label style={eyebrowStyle}>
          02 · MI LEGYEN A LEVÉL VÉGÉN?
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 items-stretch">
          {VALUE_CTA_OPTIONS.map((opt) => {
            const active = ctaId === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setCtaId(opt.id)}
                data-testid={`email-value-cta-${opt.id}`}
                style={{
                  textAlign: "left",
                  background: active ? SETUP_TEAL_BG_ACTIVE : "rgba(255,255,255,0.025)",
                  border: `1px solid ${active ? SETUP_TEAL_BORDER_ACTIVE : "rgba(255,255,255,0.08)"}`,
                  borderRadius: 12, padding: "16px 18px",
                  cursor: "pointer", fontFamily: "inherit", transition: "all 0.18s",
                  height: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-start",
                }}
              >
                <div style={{ fontSize: 13.5, fontWeight: 600, color: active ? "#cef1fb" : "#e8e8f0", marginBottom: 8 }}>
                  {opt.title}
                </div>
                <div style={{ fontSize: 12, color: "#9898ad", lineHeight: 1.5 }}>{opt.desc}</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}


/* ─── WebinarInlineSetup — phase picker + dynamic inputs ───────────────
   A Webinár promó kártya alatt inline expand-elő szekvencia setup:
     1) Phase picker: Promóció / Reminder / Follow-up / Teljes szekvencia
     2) Csak a kiválasztott módhoz szükséges inputok jelennek meg
     3) Validáció: a Generate gomb csak akkor enged, ha a kötelező mezők
        ki vannak töltve (webinar címe / következő lépés). */

const WEBINAR_MODES = [
  { id: "promo",    title: "Promóció",  meta: "5 e-mail", desc: "Építs érdeklődést, mutasd meg, miért fontos a webinár, és hívd be az érdeklődőket." },
  { id: "reminder", title: "Reminder",  meta: "5 e-mail", desc: "Emlékeztesd a regisztráltakat úgy, hogy tényleg élőben akarjanak ott lenni." },
  { id: "followup", title: "Follow-up", meta: "5 e-mail", desc: "Vidd tovább a webinár felismeréseit, kezeld a kifogásokat, és vezesd őket a következő lépéshez." },
];

// Stílus-pill opciók: 5 egyetlen univerzális single-select chip, mind a 3
// fázishoz. NEM fázisfüggő - a Writer a Márkahang+brief alapján igazítja.
const WEBINAR_STYLE_CHIPS_ALL = [
  "Sejtelmes",
  "Direkt",
  "Szakmai",
  "Finoman provokatív",
  "Könnyed",
  "Nézőpontváltó",
];

// Smart default: holnap 19:00 (`YYYY-MM-DDT19:00`). A perc fix 0, hogy a
// natív datetime-local picker `step="900"` (15 perc) lépéssel egész órán
// nyíljon - aki egész órát akar, ne kelljen a perceket külön görgetnie.
function _smartDefaultDtLocal() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}T19:00`;
}

// Helper: HTML datetime-local értékből („2026-02-18T19:00") készítünk
// emberi olvasású magyar formátumot a Writer-nek.
function formatHuDateTime(dtLocal) {
  if (!dtLocal) return "";
  const m = dtLocal.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return "";
  const [, y, mo, d, h, mi] = m;
  const months = ["január","február","március","április","május","június","július","augusztus","szeptember","október","november","december"];
  const days = ["vasárnap","hétfő","kedd","szerda","csütörtök","péntek","szombat"];
  const date = new Date(`${y}-${mo}-${d}T${h}:${mi}:00`);
  const dayName = days[date.getDay()];
  return `${y}. ${months[parseInt(mo,10)-1]} ${parseInt(d,10)}. (${dayName}), ${h}:${mi}`;
}

function WebinarInlineSetup({
  mode, setMode,
  webinarIdopont, setWebinarIdopont,
  webinarCim, setWebinarCim,
  webinarFoPontok, setWebinarFoPontok,
  foFelismeresek, setFoFelismeresek,
  kovetkezoLepes, setKovetkezoLepes,
  deadline, setDeadline,
  stilusPill, setStilusPill,
  stilusPillPromo, setStilusPillPromo,
  stilusPillFollowup, setStilusPillFollowup,
  generating,
}) {
  const eyebrowStyle = {
    fontSize: 11, color: SETUP_TEAL, letterSpacing: "0.20em",
    textTransform: "uppercase", fontWeight: 600,
    display: "block", marginBottom: 18,
  };
  const inputStyle = {
    width: "100%", background: "rgba(255,255,255,0.025)",
    border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12,
    padding: "14px 16px", fontSize: 14, color: "#e8e8f0",
    fontFamily: "inherit", outline: "none",
    resize: "vertical", lineHeight: 1.55,
  };
  const smallNoteStyle = {
    fontSize: 11.5, color: "#6a6a80", marginTop: 6, fontStyle: "italic",
  };

  const showPromoInputs = mode === "promo" || mode === "reminder";
  const showFollowupInputs = mode === "followup";
  // Mind a 3 fázishoz ugyanaz a 8 egyetlen single-select stílus-chip lista.
  const styleChips = mode ? WEBINAR_STYLE_CHIPS_ALL : [];

  // A felhasználó vagy a naptárból választ dátum+időt, vagy a szabad
  // szövegmezőbe ír egyedi formátumot ("csütörtök 19:00"). A szabad
  // szövegmező felülírja a naptárt; ha üres, a naptár értékét emberi
  // olvasású magyar formátumra alakítjuk a backend felé.
  const [dtLocal, setDtLocal] = React.useState("");
  const idopontFreeText = webinarIdopont || "";
  const onDtLocalChange = (v) => {
    setDtLocal(v);
    const formatted = formatHuDateTime(v);
    if (formatted) setWebinarIdopont(formatted);
  };

  return (
    <div
      style={{
        width: "100%", textAlign: "left",
        marginTop: 48, marginBottom: 36,
        opacity: generating ? 0.55 : 1,
        pointerEvents: generating ? "none" : "auto",
        transition: "opacity 0.22s ease",
      }}
      data-testid="email-webinar-questions"
    >
      {/* Q1 — PHASE PICKER */}
      <div style={{ marginTop: 8, marginBottom: 36 }}>
        <label style={eyebrowStyle}>
          01 · MELYIK FÁZIST ÍRJUK MEG?
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 items-stretch">
          {WEBINAR_MODES.map((opt) => {
            const active = mode === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setMode(opt.id)}
                data-testid={`email-webinar-mode-${opt.id}`}
                style={{
                  textAlign: "left",
                  background: active ? SETUP_TEAL_BG_ACTIVE : "rgba(255,255,255,0.025)",
                  border: `1px solid ${active ? SETUP_TEAL_BORDER_ACTIVE : "rgba(255,255,255,0.08)"}`,
                  borderRadius: 12, padding: "16px 18px",
                  cursor: "pointer", fontFamily: "inherit", transition: "all 0.18s",
                  height: "100%", display: "flex", flexDirection: "column",
                }}
              >
                <div className="flex items-baseline justify-between gap-2" style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: active ? "#cef1fb" : "#e8e8f0" }}>
                    {opt.title}
                  </div>
                  <div style={{ fontSize: 10.5, color: "#6f6f88", letterSpacing: "0.05em" }}>
                    {opt.meta}
                  </div>
                </div>
                <div style={{ fontSize: 11.5, color: "#9898ad", lineHeight: 1.5 }}>{opt.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Mode-specific inputs — csak akkor renderelnek, ha mode kiválasztva */}
      {mode && (
        <>
          {showPromoInputs && (
            <div style={{ marginBottom: 28 }}>
              <label style={eyebrowStyle}>
                {mode === "reminder" ? "02 · WEBINÁR CÍME / RÖVID NEVE" : "02 · WEBINÁR CÍME / TÉMÁJA"} <span style={{ color: "#ff7878" }}>*</span>
              </label>
              <input
                type="text"
                value={webinarCim}
                onChange={(e) => setWebinarCim(e.target.value)}
                placeholder={`Pl.: „Hogyan szerezz kiszámítható ügyfeleket napi posztolás nélkül"`}
                data-testid="email-webinar-cim-input"
                style={inputStyle}
              />
            </div>
          )}

          {showPromoInputs && (
            <div style={{ marginBottom: 28 }}>
              <label style={eyebrowStyle}>03 · WEBINÁR IDŐPONTJA</label>
              <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-3">
                <input
                  type="datetime-local"
                  value={dtLocal}
                  onChange={(e) => onDtLocalChange(e.target.value)}
                  onFocus={() => {
                    // Ha még nem választott a felhasználó, a picker
                    // megnyitásakor preset-eljük az óra-perc görgetőt
                    // 19:00-ra (perc = 00, kerek óra), hogy egész órás
                    // időpont esetén ne kelljen a perceket görgetnie.
                    // A `webinarIdopont` (parent state) ekkor MÉG üres
                    // marad - csak akkor frissül, ha a user ténylegesen
                    // változtat egy onChange eseménnyel.
                    if (!dtLocal) setDtLocal(_smartDefaultDtLocal());
                  }}
                  step="900"
                  data-testid="email-webinar-idopont-datetime"
                  style={{
                    ...inputStyle,
                    padding: "12px 14px",
                    colorScheme: "dark",
                  }}
                />
                <input
                  type="text"
                  value={idopontFreeText}
                  onChange={(e) => setWebinarIdopont(e.target.value)}
                  placeholder={`Vagy írj saját formátumot: „csütörtök 19:00"`}
                  data-testid="email-webinar-idopont-input"
                  style={inputStyle}
                />
              </div>
              <div style={smallNoteStyle}>Naptárból vagy szabad szöveggel - a szabad szöveg felülírja a naptárt.</div>
            </div>
          )}

          {showPromoInputs && (
            <div style={{ marginBottom: 28 }}>
              <label style={eyebrowStyle}>
                {mode === "reminder" ? "04 · 3 FŐ OK / 3 FŐ PONT" : "04 · WEBINÁR FŐ PONTJAI"}
              </label>
              <textarea
                value={webinarFoPontok}
                onChange={(e) => setWebinarFoPontok(e.target.value)}
                placeholder={`Sorold fel 2-4 mondatban a fő pontokat - mit fog megtudni az olvasó.`}
                rows={3}
                data-testid="email-webinar-pontok-input"
                style={inputStyle}
              />
            </div>
          )}

          {showFollowupInputs && (
            <div style={{ marginBottom: 28 }}>
              <label style={eyebrowStyle}>02 · 3 FŐ FELISMERÉS A WEBINÁRRÓL</label>
              <textarea
                value={foFelismeresek}
                onChange={(e) => setFoFelismeresek(e.target.value)}
                placeholder={`Mit fog észrevenni / megérteni az olvasó a webinár alatt? Add meg 3 pontban.`}
                rows={3}
                data-testid="email-webinar-felismeresek-input"
                style={inputStyle}
              />
            </div>
          )}

          {showFollowupInputs && (
            <div style={{ marginBottom: 28 }}>
              <label style={eyebrowStyle}>03 · KÖVETKEZŐ LÉPÉS</label>
              <input
                type="text"
                value={kovetkezoLepes}
                onChange={(e) => setKovetkezoLepes(e.target.value)}
                placeholder={`Telefonos konzultáció, tagság vagy ajánlat...`}
                data-testid="email-webinar-kovetkezo-input"
                style={inputStyle}
              />
              <div style={smallNoteStyle} data-testid="email-webinar-kovetkezo-helper">
                Ha üresen hagyod, vagy azt írod, hogy „ajánlat”, az aktív ajánlatodból dolgozunk.
              </div>
            </div>
          )}

          {showFollowupInputs && (
            <div style={{ marginBottom: 28 }}>
              <label style={eyebrowStyle}>
                04 · DEADLINE / BÓNUSZ / LEZÁRÁS (OPCIONÁLIS)
              </label>
              <input
                type="text"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                placeholder={`Pl.: „A jelentkezés vasárnap 23:59-ig nyitva, korai jelentkezőknek bónusz"`}
                data-testid="email-webinar-deadline-input"
                style={inputStyle}
              />
            </div>
          )}

          {/* Stílus-pill: csak Promóció és Follow-up fázishoz, single-select chip */}
          {styleChips.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <label style={eyebrowStyle}>
                {mode === "promo" ? "05" : "05"} · STÍLUS (OPCIONÁLIS)
              </label>
              <div className="flex flex-wrap gap-2.5" data-testid="email-webinar-stilus-chips">
                {styleChips.map((chip) => {
                  const active = stilusPill === chip;
                  return (
                    <button
                      key={chip}
                      type="button"
                      onClick={() => setStilusPill(active ? "" : chip)}
                      data-testid={`email-webinar-stilus-${chip.replace(/\s+/g,"-")}`}
                      data-active={active ? "true" : "false"}
                      style={{
                        fontSize: 12.5, padding: "9px 15px", borderRadius: 999,
                        background: active ? SETUP_TEAL_BG_ACTIVE : SETUP_TEAL_BG,
                        border: `1px solid ${active ? SETUP_TEAL_BORDER_ACTIVE : SETUP_TEAL_BORDER}`,
                        color: active ? "#cef1fb" : SETUP_TEAL,
                        cursor: "pointer", fontFamily: "inherit", fontWeight: 500,
                        transition: "all 0.16s",
                      }}
                    >{chip}</button>
                  );
                })}
              </div>
              <div style={{ marginTop: 8, fontSize: 11.5, color: "#6a6a80", fontStyle: "italic" }}>
                Egy stílust választhatsz - vagy hagyd üresen a Márkahangra alapozott alaphangért.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

